import { sentryMiddleware } from "@inngest/middleware-sentry";
import { Inngest } from "inngest";
import type { SendEventOutput } from "inngest/types";
import { domainContextMiddleware } from "./domain-context-middleware";
import { eventSchedulingLatencyMiddleware } from "./event-scheduling-latency-middleware";

export interface InngestDefinition {
  event: string;
  job: ReturnType<typeof inngest.createFunction>;
}

/**
 * Inngest priority levels for `priority: { run }` config.
 *
 * Higher values run before lower-priority work queued in the last N seconds.
 * Range: -600 to 600. Default (no priority) is 0.
 *
 * Use these constants instead of magic numbers so the intent is searchable.
 */
export const InngestPriority = {
  /** Job must run within seconds — a delay causes a user-visible failure.
   *  e.g. starting transcription on a live video call. */
  REALTIME: "600",

  /** Job is time-sensitive — provider or patient is likely watching for the result.
   *  e.g. prescription status during a visit, appointment booking confirmation. */
  ELEVATED: "300",
} as const;

// Create a client to send and receive events
// Sentry middleware must come first to establish the isolation scope;
// domain context middleware then sets vibrant.* tags on that scope.
export const inngest = new Inngest({
  id: "vibrant-app",
  // Slightly under Vercel max duration (800s) so checkpointing wraps before hard timeout.
  checkpointing: { maxRuntime: "790s" },
  middleware: [sentryMiddleware(), domainContextMiddleware(), eventSchedulingLatencyMiddleware()],
});

export function sendInngestEvent(
  definition: InngestDefinition,
  data: any,
  id?: string,
): Promise<SendEventOutput<{ id: string }>> {
  return inngest.send({ name: definition.event, data, ...(id && { id }) });
}
